import { Injectable, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppConfig, BYPASS_FINAL_STATUSES, StorageKey } from 'src/app/shared/constants/global-config.constant';
import { IBypassRequest } from 'src/app/shared/interfaces/interface';
import { SseEvent, SseFrameParser } from 'src/app/shared/utils/sse-parser.util';
import { DataService } from '../data/data.service';

export interface BypassRequestWatchHandlers {
  /**
   * Every status the server reports for the request, PENDING included.
   *
   * Always called OUTSIDE the Angular zone (the stream, its timers and the polls all run
   * there, so the keep-alive pings never trigger change detection). The caller must wrap
   * its handler in `ngZone.run(...)`, the same rule as the Pocketbase realtime callbacks
   * (CLAUDE.md, 2.16.1).
   */
  onStatus: (request: IBypassRequest) => void;
  /** The request cannot be followed: not this session's (404) or the session is gone (401/403). Final. */
  onError: (error: { status: number }) => void;
}

export interface BypassRequestWatch {
  /** 'stream' while on SSE, 'poll' once it fell back to polling. */
  readonly mode: 'stream' | 'poll';
  /** Stops the stream, every timer and any poll. Safe to call more than once. */
  stop(): void;
}

export interface StreamHooks {
  /** The response headers arrived (2xx, with a body). */
  onOpen?: () => void;
  /** A chunk of bytes arrived, keep-alive pings included. */
  onBytes?: () => void;
}

/** HTTP statuses after which neither reconnecting nor polling can help. */
const TERMINAL_STATUSES = [401, 403, 404];

/**
 * Follows one approval request until it reaches a final status (contract §5, D19).
 *
 * - Reads `GET /bypass/requests/:id/events` with fetch + ReadableStream, not EventSource,
 *   so the session Bearer stays in the Authorization header.
 * - Stall watchdog: a connection whose headers have not arrived within 15 s, or that has
 *   delivered no bytes at all for 60 s (the server pings every 25 s), is aborted and counts
 *   as failed (AppConfig.BYPASS_SSE_HEADERS_TIMEOUT_MS / BYPASS_SSE_IDLE_TIMEOUT_MS).
 * - When the stream fails or ends without a final status, reconnects after 1 s, 2 s, then
 *   5 s (AppConfig.BYPASS_SSE_BACKOFF_MS). A connection that delivers a status resets the count.
 * - After 3 failed reconnects in a row, polls `GET /bypass/requests/:id` every 2 s until a
 *   final status.
 * - Stops by itself on a final status; the caller stops it on destroy.
 */
@Injectable({
  providedIn: 'root'
})
export class BypassRequestStreamService {

  constructor(
    private dataService: DataService,
    private ngZone: NgZone,
  ) { }

  watch(id: number, handlers: BypassRequestWatchHandlers): BypassRequestWatch {
    const watch = new RequestWatch(id, handlers, this, this.dataService);
    this.ngZone.runOutsideAngular(() => watch.start());
    return watch;
  }

  /**
   * Opens the event stream and hands every complete frame to `onEvent`. Resolves when the
   * server closes the stream; rejects with `{ status }` on a non-2xx answer, on a network
   * error, or when `signal` aborts it. `hooks.onOpen` fires once the headers are in,
   * `hooks.onBytes` on every chunk (pings included), for the stall watchdog.
   */
  async openStream(
    url: string,
    signal: AbortSignal,
    onEvent: (event: SseEvent) => void,
    hooks: StreamHooks = {},
  ): Promise<void> {
    const response = await this.fetchFn(url, {
      method: 'GET',
      headers: {
        'Accept': 'text/event-stream',
        'Authorization': `Bearer ${localStorage.getItem(StorageKey.SESSION_ID)}`,
      },
      cache: 'no-store',
      signal,
    });
    if (!response.ok) {
      throw { status: response.status };
    }
    if (!response.body) {
      // No streaming body in this browser: counts as a failed connection, so it ends up polling.
      throw { status: 0 };
    }
    hooks.onOpen?.();
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseFrameParser();
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // An unterminated frame at the end of the stream is discarded, as the SSE spec says.
        return;
      }
      hooks.onBytes?.();
      for (const event of parser.push(decoder.decode(value, { stream: true }))) {
        onEvent(event);
      }
    }
  }

  /** Overridable in tests. */
  fetchFn: typeof fetch = (input, init) => fetch(input, init);
}

class RequestWatch implements BypassRequestWatch {

  mode: 'stream' | 'poll' = 'stream';
  private stopped = false;
  /** Failed reconnects in a row. */
  private failures = 0;
  private abort: AbortController | null = null;
  private timer: any = null;
  private pollSub: Subscription | null = null;
  /** The current connection's stall watchdog (headers, then bytes). */
  private stallTimer: any = null;

  constructor(
    private id: number,
    private handlers: BypassRequestWatchHandlers,
    private service: BypassRequestStreamService,
    private dataService: DataService,
  ) { }

  start() {
    this.connect();
  }

  stop() {
    this.stopped = true;
    this.clearStall();
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.abort) {
      this.abort.abort();
      this.abort = null;
    }
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
  }

  private connect() {
    if (this.stopped) {
      return;
    }
    this.mode = 'stream';
    const abort = new AbortController();
    this.abort = abort;
    let connected = false;
    let ended = false;
    const onEnded = (err?: any) => {
      if (ended) {
        return;
      }
      ended = true;
      this.clearStall();
      if (this.abort === abort) {
        this.abort = null;
      }
      this.onStreamEnded(err);
    };
    // Stalled: abort it and count it as a failed connection (backoff, then polling).
    const armStall = (ms: number) => {
      this.clearStall();
      this.stallTimer = setTimeout(() => {
        this.stallTimer = null;
        if (ended || this.stopped) {
          return;
        }
        abort.abort();
        onEnded({ status: 0 });
      }, ms);
    };
    const markAlive = () => {
      if (!ended && !this.stopped) {
        armStall(AppConfig.BYPASS_SSE_IDLE_TIMEOUT_MS);
      }
    };
    armStall(AppConfig.BYPASS_SSE_HEADERS_TIMEOUT_MS);
    let opened: Promise<void>;
    try {
      opened = this.service.openStream(this.dataService.getBypassRequestEventsUrl(this.id), abort.signal, event => {
        if (this.stopped || event.event !== 'status') {
          return;
        }
        let request: IBypassRequest | undefined;
        try {
          request = JSON.parse(event.data)?.request;
        }
        catch (err) {
          return;
        }
        if (!request) {
          return;
        }
        if (!connected) {
          connected = true;
          this.failures = 0;
        }
        this.deliver(request);
      }, { onOpen: markAlive, onBytes: markAlive });
    }
    catch (err) {
      onEnded(err);
      return;
    }
    opened.then(() => onEnded(), err => onEnded(err ?? {}));
  }

  private clearStall() {
    if (this.stallTimer) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  }

  private onStreamEnded(err?: any) {
    if (this.stopped) {
      return;
    }
    if (err && TERMINAL_STATUSES.includes(err.status)) {
      this.fail(err);
      return;
    }
    const backoff = AppConfig.BYPASS_SSE_BACKOFF_MS;
    if (this.failures >= backoff.length) {
      this.startPolling();
      return;
    }
    const delay = backoff[this.failures];
    this.failures++;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.connect();
    }, delay);
  }

  private startPolling() {
    this.mode = 'poll';
    this.poll();
  }

  private poll() {
    if (this.stopped) {
      return;
    }
    this.pollSub = this.dataService.getBypassRequest(this.id).subscribe({
      next: response => {
        const request = response?.data?.request;
        if (request) {
          this.deliver(request);
        }
        this.schedulePoll();
      },
      error: err => {
        if (TERMINAL_STATUSES.includes(err?.status)) {
          this.fail(err);
          return;
        }
        this.schedulePoll();
      },
    });
  }

  private schedulePoll() {
    if (this.stopped) {
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      this.poll();
    }, AppConfig.BYPASS_POLL_INTERVAL_MS);
  }

  private deliver(request: IBypassRequest) {
    if (BYPASS_FINAL_STATUSES.includes(request.status)) {
      this.stop();
    }
    this.handlers.onStatus(request);
  }

  private fail(err: any) {
    this.stop();
    this.handlers.onError({ status: err?.status ?? 0 });
  }
}
